import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AiBadge } from '@/components/ai/AiBadge';
import { runAi } from '@/lib/ai';
import { carrierBriefDemo } from '@/lib/carrierBriefDemo';
import { t, getLanguage } from '@/i18n';
import { Loader2, Sparkles } from 'lucide-react';

const pick = ({ loadings, announcedPct, noShowPct, outOfHours, replyMedian, onTimePct }) =>
  ({ loadings, announcedPct, noShowPct, outOfHours, replyMedian, onTimePct });

const List = ({ title, items }) => (
  <section className="space-y-1">
    <h3 className="text-sm font-semibold">{title}</h3>
    <ul className="list-disc pl-5 text-sm space-y-0.5">{items.map(i => <li key={i}>{i}</li>)}</ul>
  </section>
);

// Talking points for the meeting with the carrier (#73); printed with the rating.
export const MeetingBrief = ({ carrier, rating }) => {
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);

  const prepare = async () => {
    setBusy(true);
    const input = {
      lang: getLanguage(), month: rating.month,
      carrier: { name: carrier.name, country: carrier.country, fleet: carrier.fleet },
      now: pick(rating.now), prev: rating.prev ? pick(rating.prev) : null,
    };
    setBrief(await runAi('carrier-brief', input, carrierBriefDemo));
    setBusy(false);
  };

  if (!brief) {
    return (
      <Button variant="outline" className="gap-1 w-full print:hidden" onClick={prepare} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {t('ai.brief.prepare')}
      </Button>
    );
  }
  const b = brief.result;
  return (
    <article className="bg-card rounded-lg border p-6 sm:p-8 space-y-4 print:border-0 print:p-0 print:break-before-page">
      <header className="flex items-center gap-2">
        <h2 className="text-lg font-bold">{t('ai.brief.title')}</h2>
        <AiBadge mode={brief.mode} className="ml-auto print:hidden" />
      </header>
      <p className="text-sm font-medium">{t('ai.brief.goal')}: {b.goal}</p>
      <List title={t('ai.brief.arguments')} items={b.arguments} />
      <section className="space-y-1">
        <h3 className="text-sm font-semibold">{t('ai.brief.proposals')}</h3>
        <dl className="text-sm space-y-1">
          {b.proposals.map(p => (
            <div key={p.term}><dt className="font-medium inline">{p.term}: </dt><dd className="inline">{p.detail}</dd></div>
          ))}
        </dl>
      </section>
      <List title={t('ai.brief.agreements')} items={b.agreements} />
      <p className="text-[11px] text-muted-foreground border-t pt-2">{t('ai.brief.note')}</p>
    </article>
  );
};
